/**
 * 調停手続きテンプレートサービス
 * 調停・あっせん関連の書類テンプレートを管理し、プレビューとダウンロード機能を提供
 */

export interface TemplateDocument {
  id: string;
  name: string;
  type: 'md' | 'html' | 'pdf';
  description: string;
  category: string;
  content?: string;
  filePath?: string;
}

export interface TemplateCategory {
  id: string;
  name: string;
  description: string;
  documents: TemplateDocument[];
}

/**
 * 調停手続きテンプレートデータ
 */
export const mediationTemplates: TemplateCategory[] = [
  {
    id: 'application',
    name: '申立書類',
    description: '調停申立に必要な書類',
    documents: [
      {
        id: 'civil_mediation_application',
        name: '調停申立書テンプレート',
        type: 'md',
        description: '簡易裁判所民事調停申立書の標準書式',
        category: '申立書類',
        filePath: '/templates/mediation/調停申立書テンプレート.md'
      },
      {
        id: 'family_mediation_application',
        name: '家庭裁判所調停申立書',
        type: 'md',
        description: '家事調停申立書の標準書式',
        category: '申立書類',
        filePath: '/templates/mediation/家庭裁判所調停申立書.md'
      }
    ]
  },
  {
    id: 'preparation',
    name: '準備書類',
    description: '調停準備に必要な書類',
    documents: [
      {
        id: 'evidence_list',
        name: '証拠書類リスト',
        type: 'md',
        description: '調停に必要な証拠書類のチェックリスト',
        category: '準備書類',
        filePath: '/templates/mediation/証拠書類リスト.md'
      },
      {
        id: 'mediation_preparation_memo',
        name: '調停期日準備メモ',
        type: 'md',
        description: '調停期日での話し合いの準備用メモ',
        category: '準備書類',
        filePath: '/templates/mediation/調停期日準備メモ.md'
      }
    ]
  },
  {
    id: 'procedure',
    name: '手続書類',
    description: '手続き関連の書類',
    documents: [
      {
        id: 'fee_calculation',
        name: '手数料計算表',
        type: 'md',
        description: '調停手数料の計算方法と納付方法',
        category: '手続書類',
        filePath: '/templates/mediation/手数料計算表.md'
      }
    ]
  },
  {
    id: 'agreement',
    name: '合意書類',
    description: '合意成立時の書類',
    documents: [
      {
        id: 'agreement_template',
        name: '合意書テンプレート',
        type: 'md',
        description: '調停成立時の合意書作成用テンプレート',
        category: '合意書類',
        filePath: '/templates/mediation/合意書テンプレート.md'
      }
    ]
  }
];

/**
 * 自己訴訟文書テンプレートデータ
 */
export const litigationTemplates: TemplateCategory[] = [
  {
    id: 'complaint',
    name: '訴状関連',
    description: '訴訟提起に必要な書類',
    documents: [
      {
        id: 'complaint_template',
        name: '訴状テンプレート',
        type: 'md',
        description: '民事訴訟の訴状の標準書式',
        category: '訴状関連',
        filePath: '/templates/litigation/訴状テンプレート.md'
      }
    ]
  },
  {
    id: 'answer',
    name: '答弁書関連',
    description: '被告側の答弁に必要な書類',
    documents: [
      {
        id: 'answer_template',
        name: '答弁書テンプレート',
        type: 'md',
        description: '被告側の答弁書の標準書式',
        category: '答弁書関連',
        filePath: '/templates/litigation/答弁書テンプレート.md'
      }
    ]
  },
  {
    id: 'preparation',
    name: '準備書面関連',
    description: '口頭弁論の準備に必要な書類',
    documents: [
      {
        id: 'preparation_template',
        name: '準備書面テンプレート',
        type: 'md',
        description: '口頭弁論の準備書面',
        category: '準備書面関連',
        filePath: '/templates/litigation/準備書面テンプレート.md'
      }
    ]
  },
  {
    id: 'procedure',
    name: '手続書類',
    description: '訴訟手続き関連の書類',
    documents: [
      {
        id: 'evidence_request',
        name: '証拠調べ申出書',
        type: 'md',
        description: '証拠調べの申出書',
        category: '手続書類',
        filePath: '/templates/litigation/証拠調べ申出書.md'
      },
      {
        id: 'date_change_request',
        name: '期日変更申出書',
        type: 'md',
        description: '期日の変更申出書',
        category: '手続書類',
        filePath: '/templates/litigation/期日変更申出書.md'
      },
      {
        id: 'settlement_request',
        name: '和解申出書',
        type: 'md',
        description: '和解の申出書',
        category: '手続書類',
        filePath: '/templates/litigation/和解申出書.md'
      }
    ]
  },
  {
    id: 'appeal',
    name: '上訴関連',
    description: '上訴手続きに必要な書類',
    documents: [
      {
        id: 'appeal_template',
        name: '控訴申立書',
        type: 'md',
        description: '控訴申立書',
        category: '上訴関連',
        filePath: '/templates/litigation/控訴申立書.md'
      }
    ]
  }
];

/**
 * 弁護士依頼関連テンプレートデータ
 */
export const lawyerTemplates: TemplateCategory[] = [
  {
    id: 'entrustment',
    name: '委任関連',
    description: '弁護士への委任に必要な書類',
    documents: [
      {
        id: 'power_of_attorney',
        name: '委任状テンプレート',
        type: 'md',
        description: '弁護士への正式な委任状',
        category: '委任関連',
        filePath: '/templates/lawyer/委任状テンプレート.md'
      },
      {
        id: 'contract_template',
        name: '契約書テンプレート',
        type: 'md',
        description: '弁護士との委任契約書',
        category: '委任関連',
        filePath: '/templates/lawyer/契約書テンプレート.md'
      }
    ]
  },
  {
    id: 'consultation',
    name: '相談・依頼関連',
    description: '弁護士相談・依頼に必要な書類',
    documents: [
      {
        id: 'consultation_memo',
        name: '相談メモテンプレート',
        type: 'md',
        description: '初回相談時の準備メモ',
        category: '相談・依頼関連',
        filePath: '/templates/lawyer/相談メモテンプレート.md'
      },
      {
        id: 'request_letter',
        name: '依頼書テンプレート',
        type: 'md',
        description: '弁護士への依頼内容書',
        category: '相談・依頼関連',
        filePath: '/templates/lawyer/依頼書テンプレート.md'
      }
    ]
  },
  {
    id: 'selection',
    name: '選定・準備関連',
    description: '弁護士選定・相談準備に必要な書類',
    documents: [
      {
        id: 'lawyer_selection_checklist',
        name: '弁護士選定チェックリスト',
        type: 'md',
        description: '弁護士選定時のチェックリスト',
        category: '選定・準備関連',
        filePath: '/templates/lawyer/弁護士選定チェックリスト.md'
      },
      {
        id: 'consultation_preparation_sheet',
        name: '相談準備シート',
        type: 'md',
        description: '相談前の準備事項',
        category: '選定・準備関連',
        filePath: '/templates/lawyer/相談準備シート.md'
      },
      {
        id: 'fee_estimate',
        name: '費用見積もり表',
        type: 'md',
        description: '弁護士費用の見積もり表',
        category: '選定・準備関連',
        filePath: '/templates/lawyer/費用見積もり表.md'
      }
    ]
  }
];

/**
 * テンプレートサービスクラス
 */
export class TemplateService {
  /**
   * 指定されたカテゴリのテンプレートを取得
   */
  static getTemplatesByCategory(categoryId: string, templateType: 'mediation' | 'litigation' | 'lawyer' = 'mediation'): TemplateDocument[] {
    const templates = this.getTemplatesByType(templateType);
    const category = templates.find(cat => cat.id === categoryId);
    return category ? category.documents : [];
  }

  /**
   * すべてのテンプレートを取得
   */
  static getAllTemplates(templateType: 'mediation' | 'litigation' | 'lawyer' = 'mediation'): TemplateDocument[] {
    const templates = this.getTemplatesByType(templateType);
    return templates.flatMap(category => category.documents);
  }

  /**
   * すべてのテンプレート（調停・自己訴訟・弁護士依頼）を取得
   */
  static getAllTemplatesCombined(): TemplateDocument[] {
    return [
      ...this.getAllTemplates('mediation'), 
      ...this.getAllTemplates('litigation'),
      ...this.getAllTemplates('lawyer')
    ];
  }

  /**
   * テンプレートタイプに応じたテンプレート配列を取得
   */
  private static getTemplatesByType(templateType: 'mediation' | 'litigation' | 'lawyer'): TemplateCategory[] {
    switch (templateType) {
      case 'mediation':
        return mediationTemplates;
      case 'litigation':
        return litigationTemplates;
      case 'lawyer':
        return lawyerTemplates;
      default:
        return mediationTemplates;
    }
  }

  /**
   * 指定されたIDのテンプレートを取得
   */
  static getTemplateById(templateId: string): TemplateDocument | undefined {
    return this.getAllTemplatesCombined().find(doc => doc.id === templateId);
  }

  /**
   * テンプレートの内容を読み込み
   */
  static async loadTemplateContent(templateId: string): Promise<string> {
    const template = this.getTemplateById(templateId);
    if (!template) {
      throw new Error(`テンプレートが見つかりません: ${templateId}`);
    }

    // まずpublicディレクトリのファイルを試す
    if (template.filePath) {
      try {
        // console.log('Publicディレクトリからファイルを読み込み中:', template.filePath);
        const response = await fetch(template.filePath);
        if (response.ok) {
          const content = await response.text();
          // console.log('Publicディレクトリからファイルを読み込み成功');
          return content;
        }
      } catch (error) {
        console.warn('Publicディレクトリからの読み込みに失敗、内联内容を使用:', error);
      }
    }

    // フォールバック：内联模板内容
    const templateContents: Record<string, string> = {
      // 調停関連テンプレート
      'civil_mediation_application': `# 調停申立書

## 申立人情報
- 氏名：_________________
- 住所：_________________
- 電話番号：_____________

## 相手方情報
- 氏名：_________________
- 住所：_________________

## 調停の趣旨
_________________

## 紛争の事実
_________________

## 証拠書類
- _________________
- _________________

## 申立日
_________________`,

      // 自己訴訟関連テンプレート
      'complaint_template': `# 訴状

## 裁判所名
_________________地方裁判所

## 原告
- 氏名：_________________
- 住所：_________________
- 電話番号：_____________

## 被告
- 氏名：_________________
- 住所：_________________

## 訴訟物の価額
金_________________円

## 訴額
金_________________円

## 請求の趣旨
被告に対し、金_________________円及びこれに対する平成___年___月___日から支払済みまで年___分の割合による遅延損害金の支払を命ずる。

## 請求の原因
1. _________________
2. _________________
3. _________________

## 証拠
1. 書証
   - _________________
   - _________________
2. 人証
   - _________________

## 添付書類
1. 訴額計算書（原本1通、副本___通）
2. 委任状（原本1通、副本___通）
3. その他：_________________

## 提出日
平成___年___月___日

## 提出者
原告　_________________　印`,

      'answer_template': `# 答弁書

## 裁判所名
_________________地方裁判所

## 事件番号
平成___年（ワ）第___号

## 事件名
_________________事件

## 被告
- 氏名：_________________
- 住所：_________________
- 電話番号：_____________

## 原告
- 氏名：_________________
- 住所：_________________

## 答弁の趣旨
原告の請求を棄却する。

## 答弁の理由
1. **原告の主張の否認**
   - 原告の主張する事実を否認する
   - 具体的な否認理由：_________________

2. **抗弁事実**
   - _________________
   - _________________

3. **証拠**
   - 書証：_________________
   - 人証：_________________

## 添付書類
1. 委任状（原本1通、副本___通）
2. その他：_________________

## 提出日
平成___年___月___日

## 提出者
被告　_________________　印`,

      'preparation_template': `# 準備書面

## 裁判所名
_________________地方裁判所

## 事件番号
平成___年（ワ）第___号

## 事件名
_________________事件

## 提出者
_________________（原告・被告）

## 相手方
_________________

## 準備書面の趣旨
口頭弁論の準備のため、以下の準備書面を提出する。

## 主張の要旨
1. **事実関係**
   - _________________
   - _________________

2. **法的評価**
   - _________________
   - _________________

3. **証拠関係**
   - 提出証拠：_________________
   - 証拠の説明：_________________

## 争点の整理
1. **争いのない事実**
   - _________________
   - _________________

2. **争いのある事実**
   - _________________
   - _________________

3. **法的争点**
   - _________________
   - _________________

## 立証計画
1. **第1回期日**
   - 証拠：_________________
   - 主張：_________________

2. **第2回期日**
   - 証拠：_________________
   - 主張：_________________

## 添付書類
1. 証拠書類（原本1通、副本___通）
2. その他：_________________

## 提出日
平成___年___月___日

## 提出者
_________________　印`,

      'evidence_request': `# 証拠調べ申出書

## 裁判所名
_________________地方裁判所

## 事件番号
平成___年（ワ）第___号

## 事件名
_________________事件

## 申出人
_________________（原告・被告）

## 相手方
_________________

## 申出の趣旨
以下の証拠調べを申し出る。

## 申出証拠
1. **書証**
   - 証拠番号：甲第___号
   - 証拠の名称：_________________
   - 証明すべき事実：_________________
   - 提出方法：原本提出

2. **人証**
   - 証拠番号：乙第___号
   - 証人氏名：_________________
   - 証明すべき事実：_________________
   - 尋問事項：_________________

3. **鑑定**
   - 証拠番号：丙第___号
   - 鑑定事項：_________________
   - 鑑定人：_________________

## 証拠の説明
1. **書証の説明**
   - 作成者：_________________
   - 作成日：_________________
   - 内容の要約：_________________

2. **人証の説明**
   - 証人との関係：_________________
   - 証拠価値：_________________

## 添付書類
1. 証拠書類（原本1通、副本___通）
2. その他：_________________

## 提出日
平成___年___月___日

## 提出者
_________________　印`,

      'date_change_request': `# 期日変更申出書

## 裁判所名
_________________地方裁判所

## 事件番号
平成___年（ワ）第___号

## 事件名
_________________事件

## 申出人
_________________（原告・被告）

## 相手方
_________________

## 申出の趣旨
平成___年___月___日午前___時から開かれる口頭弁論期日を変更することを申し出る。

## 変更を希望する期日
平成___年___月___日午前___時から

## 変更の理由
1. **具体的な理由**
   - _________________
   - _________________

2. **緊急性の有無**
   - 緊急：□ 有　□ 無
   - 理由：_________________

3. **相手方との調整状況**
   - 調整済み：□ 是　□ 否
   - 調整内容：_________________

## 代替期日の候補
1. 第1希望：平成___年___月___日午前___時から
2. 第2希望：平成___年___月___日午前___時から
3. 第3希望：平成___年___月___日午前___時から

## 添付書類
1. 相手方の同意書（原本1通、副本___通）
2. その他：_________________

## 提出日
平成___年___月___日

## 提出者
_________________　印`,

      'settlement_request': `# 和解申出書

## 裁判所名
_________________地方裁判所

## 事件番号
平成___年（ワ）第___号

## 事件名
_________________事件

## 申出人
_________________（原告・被告）

## 相手方
_________________

## 申出の趣旨
本件について和解を申し出る。

## 和解の内容
1. **和解条件**
   - _________________
   - _________________

2. **履行方法**
   - _________________
   - _________________

3. **履行期限**
   - _________________

## 和解の理由
1. **和解を希望する理由**
   - _________________
   - _________________

2. **和解による解決のメリット**
   - _________________
   - _________________

## 和解の手続
1. **和解期日の希望**
   - 第1希望：平成___年___月___日
   - 第2希望：平成___年___月___日
   - 第3希望：平成___年___月___日

2. **和解の方法**
   - 裁判所和解：□ 希望　□ 不要
   - 私的和解：□ 希望　□ 不要

## 添付書類
1. 和解案（原本1通、副本___通）
2. その他：_________________

## 提出日
平成___年___月___日

## 提出者
_________________　印`,

      'appeal_template': `# 控訴申立書

## 控訴裁判所名
_________________高等裁判所

## 原裁判所名
_________________地方裁判所

## 原事件番号
平成___年（ワ）第___号

## 控訴事件番号
平成___年（ネ）第___号

## 控訴人
- 氏名：_________________
- 住所：_________________
- 電話番号：_____________

## 被控訴人
- 氏名：_________________
- 住所：_________________

## 控訴の趣旨
原判決を破棄し、被控訴人に対し、金_________________円及びこれに対する平成___年___月___日から支払済みまで年___分の割合による遅延損害金の支払を命ずる。

## 控訴の理由
1. **事実認定の誤り**
   - _________________
   - _________________

2. **法令適用の誤り**
   - _________________
   - _________________

3. **手続違法**
   - _________________
   - _________________

## 新たな証拠
1. 書証
   - _________________
   - _________________
2. 人証
   - _________________

## 添付書類
1. 原判決正本（原本1通、副本___通）
2. 委任状（原本1通、副本___通）
3. その他：_________________

## 提出日
平成___年___月___日

## 提出者
控訴人　_________________　印`,

      'family_mediation_application': `# 家庭裁判所調停申立書

## 申立人
- 氏名：_________________
- 生年月日：_____________
- 住所：_________________

## 相手方
- 氏名：_________________
- 生年月日：_____________
- 住所：_________________

## 調停の種類
□ 離婚調停
□ 相続調停
□ 親権調停
□ その他：_____________

## 調停の趣旨
_________________

## 紛争の経緯
_________________

## 添付書類
- _________________
- _________________`,

      'evidence_list': `# 証拠書類リスト

## 契約書類
- [ ] 契約書
- [ ] 見積書
- [ ] 請求書
- [ ] 領収書

## 通信記録
- [ ] メール
- [ ] 手紙
- [ ] 電話記録

## 写真・動画
- [ ] 現場写真
- [ ] 損害写真
- [ ] 録音・録画

## その他
- [ ] 証人証言
- [ ] 専門家意見書
- [ ] その他：_____________

## 注意事項
- 原本と写しを準備
- 日付順に整理
- 重要度順に並べ替え`,

      'mediation_preparation_memo': `# 調停期日準備メモ

## 調停期日
日時：_________________
場所：_________________

## 話し合いのポイント
1. _________________
2. _________________
3. _________________

## 準備する資料
- [ ] 証拠書類
- [ ] メモ用紙
- [ ] 筆記用具

## 話し合いの流れ
1. 調停委員の説明
2. 双方の主張
3. 事実確認
4. 合意案の検討
5. 合意書の作成

## 注意点
- 冷静に話し合う
- 相手の話をよく聞く
- 感情的にならない
- 具体的な解決案を提示`,

      'fee_calculation': `# 調停手数料計算表

## 民事調停手数料
| 申立額 | 手数料 |
|--------|--------|
| 100万円以下 | 1,000円 |
| 100万円超～500万円以下 | 2,000円 |
| 500万円超～1,000万円以下 | 3,000円 |
| 1,000万円超～5,000万円以下 | 5,000円 |
| 5,000万円超 | 10,000円 |

## 家事調停手数料
- 離婚調停：1,200円
- 相続調停：1,200円
- 親権調停：1,200円

## 納付方法
1. 収入印紙を購入
2. 申立書に貼付
3. 窓口または郵送で提出

## 還付・免除
- 生活保護受給者
- 低所得者
- その他：裁判所に相談`,

      'agreement_template': `# 調停調書（合意書）

## 調停成立日
_________________

## 当事者
申立人：_________________
相手方：_________________

## 合意内容
1. _________________
2. _________________
3. _________________

## 履行方法
_________________

## 履行期限
_________________

## 違約の場合の措置
_________________

## 署名・捺印
申立人：_________________ 印
相手方：_________________ 印
調停委員：_________________ 印

## 注意事項
- この合意書は確定判決と同一の効力を有します
- 履行しない場合は強制執行が可能です
- 内容を変更する場合は再度調停が必要です`,

      // 弁護士依頼関連テンプレート
      'power_of_attorney': `# 委任状

## 委任人
- 氏名：_________________
- 住所：_________________
- 電話番号：_____________
- 生年月日：_____________

## 受任弁護士
- 氏名：_________________
- 所属：_________________
- 住所：_________________
- 電話番号：_____________

## 委任の趣旨
委任人は、受任弁護士に対し、以下の事件について弁護士業務を委任する。

## 委任事件
- 事件名：_________________
- 事件番号：_____________
- 相手方：_________________
- 事件の概要：_________________

## 委任の範囲
1. **法律相談**
   - 事件に関する法的アドバイス
   - 解決方法の検討
   - 手続きの説明

2. **書類作成**
   - 訴状、答弁書等の訴訟書類
   - 契約書、合意書等の法的文書
   - その他必要な書類

3. **交渉・手続き**
   - 相手方との交渉
   - 裁判所等での手続き
   - 調停・和解の代理

4. **その他**
   - 証拠収集のアドバイス
   - 専門家への照会
   - 関係機関との連絡

## 委任期間
- 開始日：_________________
- 終了日：_________________（事件解決まで）

## 報酬
1. **相談料**
   - 初回相談：_________________円
   - 継続相談：_________________円/時間

2. **着手金**
   - 金額：_________________円
   - 支払期限：_________________

3. **成功報酬**
   - 解決金額の_________________%
   - 支払条件：_________________

4. **その他費用**
   - 交通費：実費
   - 通信費：実費
   - 印紙代：実費

## 支払方法
- 方法：_________________
- 期限：_________________
- 振込先：_________________

## 解約条件
1. 委任人は、受任弁護士に書面で通知することにより、いつでも委任を解除できる
2. 受任弁護士は、正当な事由がある場合に限り、委任を辞任できる
3. 解約時の費用精算は、別途協議する

## 守秘義務
受任弁護士は、委任人から知り得た情報について、法律に定める場合を除き、第三者に漏洩しない

## その他
- 特記事項：_________________
- 連絡方法：_________________
- 緊急時連絡先：_________________

## 作成日
_________________

## 署名・捺印
委任人：_________________ 印
受任弁護士：_________________ 印

## 注意事項
- この委任状は、弁護士法に基づく正式な委任状です
- 委任の範囲は、事件の性質に応じて調整してください
- 報酬については、事前に十分に協議してください
- 不明な点があれば、弁護士に確認してください`,

      'contract_template': `# 弁護士委任契約書

## 契約当事者
### 委任人（依頼人）
- 氏名：_________________
- 住所：_________________
- 電話番号：_____________
- 生年月日：_____________

### 受任弁護士
- 氏名：_________________
- 所属：_________________
- 住所：_________________
- 電話番号：_____________

## 第1条（委任の趣旨）
委任人は、受任弁護士に対し、以下の事件について弁護士業務を委任し、受任弁護士はこれを受任する。

## 第2条（委任事件）
### 事件名
_________________

### 事件番号
_________________

### 相手方
- 氏名・名称：_________________
- 住所：_________________
- 連絡先：_________________

### 事件の概要
_________________

## 第3条（委任の範囲）
受任弁護士は、以下の業務を行う。

### 1. 法律相談
- 事件に関する法的アドバイス
- 解決方法の検討
- 手続きの説明

### 2. 書類作成
- 訴状、答弁書等の訴訟書類
- 契約書、合意書等の法的文書
- その他必要な書類

### 3. 交渉・手続き
- 相手方との交渉
- 裁判所等での手続き
- 調停・和解の代理

### 4. その他
- 証拠収集のアドバイス
- 専門家への照会
- 関係機関との連絡

## 第4条（委任期間）
### 開始日
_________________

### 終了日
_________________（事件解決まで）

### 更新
本契約は、事件の解決まで継続する。ただし、当事者の合意により、いつでも更新または変更することができる。

## 第5条（報酬）
### 1. 相談料
- 初回相談：_________________円
- 継続相談：_________________円/時間

### 2. 着手金
- 金額：_________________円
- 支払期限：_________________
- 支払方法：_________________

### 3. 成功報酬
- 解決金額の_________________%
- 支払条件：_________________
- 最低額：_________________円
- 最高額：_________________円

### 4. その他費用
- 交通費：実費
- 通信費：実費
- 印紙代：実費
- その他：_________________

## 第6条（支払方法）
### 支払方法
- 銀行振込：_________________
- 現金：_________________
- その他：_________________

### 支払期限
- 着手金：_________________
- 月額費用：_________________
- 成功報酬：_________________

## 第7条（報告義務）
### 受任弁護士の報告義務
受任弁護士は、事件の進行状況について、委任人に定期的に報告する。

### 報告の方法
- 報告方法：_________________
- 報告頻度：_________________
- 報告内容：_________________

## 第8条（守秘義務）
受任弁護士は、委任人から知り得た情報について、法律に定める場合を除き、第三者に漏洩しない。

## 第9条（解約）
### 委任人の解除権
委任人は、受任弁護士に書面で通知することにより、いつでも委任を解除できる。

### 受任弁護士の辞任権
受任弁護士は、正当な事由がある場合に限り、委任を辞任できる。

### 解約時の費用精算
解約時の費用精算は、別途協議する。

## 第10条（損害賠償）
当事者は、本契約の履行に関し、相手方に損害を与えた場合、その損害を賠償する。

## 第11条（協議事項）
本契約に定めのない事項については、当事者が誠実に協議して解決する。

## 第12条（準拠法・管轄裁判所）
### 準拠法
本契約は、日本法に準拠する。

### 管轄裁判所
本契約に関する紛争については、_________________地方裁判所を第一審の専属管轄裁判所とする。

## 第13条（契約書の作成）
本契約は、正本2通を作成し、当事者が署名捺印の上、各自1通を保有する。

## 特記事項
_________________

## 契約締結日
_________________

## 署名・捺印
委任人：_________________ 印
受任弁護士：_________________ 印

## 注意事項
- この契約書は、弁護士法に基づく正式な委任契約書です
- 委任の範囲は、事件の性質に応じて調整してください
- 報酬については、事前に十分に協議してください
- 不明な点があれば、弁護士に確認してください
- 契約内容は、事件の進行に応じて変更される場合があります`,

      'consultation_memo': `# 弁護士相談メモ

## 相談日時
- 日付：_________________
- 時間：_________________
- 場所：_________________

## 相談者情報
- 氏名：_________________
- 年齢：_________________
- 職業：_________________
- 連絡先：_________________

## 相談内容の概要
### 事件の種類
□ 民事事件（契約トラブル、損害賠償等）
□ 家事事件（離婚、相続等）
□ 労働事件（解雇、残業代等）
□ 刑事事件
□ その他：_________________

### 相手方
- 氏名・名称：_________________
- 住所：_________________
- 連絡先：_________________
- 関係：_________________

## 紛争の経緯
### 発端
_________________

### 経過
1. _________________
2. _________________
3. _________________

### 現在の状況
_________________

### 証拠書類
- [ ] 契約書
- [ ] 請求書・領収書
- [ ] メール・手紙
- [ ] 写真・録音
- [ ] その他：_________________

## 希望する解決方法
□ 和解・調停
□ 訴訟
□ その他：_________________

## 予算・期限
- 予算：_________________円
- 希望解決期限：_________________
- 緊急度：□ 高　□ 中　□ 低

## 質問事項
1. _________________
2. _________________
3. _________________
4. _________________
5. _________________

## 弁護士への要望
- 専門分野：_________________
- 対応可能時間：_________________
- その他：_________________

## 相談後の行動計画
1. _________________
2. _________________
3. _________________

## メモ
_________________

## 次回相談予定
- 日時：_________________
- 準備事項：_________________

## 注意事項
- 相談内容は守秘義務の対象です
- 証拠書類は事前に整理しておいてください
- 質問事項は事前にまとめておいてください
- 相談料について事前に確認してください`,

      'request_letter': `# 弁護士依頼書

## 依頼人
- 氏名：_________________
- 住所：_________________
- 電話番号：_____________
- メールアドレス：_________________

## 依頼先弁護士
- 氏名：_________________
- 所属：_________________
- 住所：_________________
- 電話番号：_____________

## 依頼の趣旨
上記依頼人は、下記の事件について、上記弁護士に法律業務を依頼する。

## 依頼事件の詳細
### 事件名
_________________

### 事件の概要
_________________

### 相手方
- 氏名・名称：_________________
- 住所：_________________
- 連絡先：_________________

### 事件の経緯
1. _________________
2. _________________
3. _________________

### 現在の状況
_________________

## 依頼内容
### 主要な依頼事項
1. **法的アドバイス**
   - 事件の法的評価
   - 解決方法の検討
   - リスクの分析

2. **書類作成**
   - 訴状・答弁書等の訴訟書類
   - 契約書・合意書等の法的文書
   - その他必要な書類

3. **交渉・手続き**
   - 相手方との交渉
   - 裁判所等での手続き
   - 調停・和解の代理

4. **証拠収集**
   - 証拠の収集・整理
   - 専門家への照会
   - 関係機関との連絡

### 具体的な要望
1. _________________
2. _________________
3. _________________

## 希望する解決方法
□ 和解・調停による解決
□ 訴訟による解決
□ その他：_________________

## 予算・期限
### 予算
- 総予算：_________________円
- 月額予算：_________________円
- その他：_________________

### 期限
- 希望解決期限：_________________
- 緊急度：□ 高　□ 中　□ 低
- 特定期限：_________________

## 報酬について
### 希望する報酬体系
□ 時給制
□ 着手金＋成功報酬制
□ 定額制
□ その他：_________________

### 具体的な希望
- 相談料：_________________円/時間
- 着手金：_________________円
- 成功報酬：解決金額の_________________%
- その他：_________________

## 連絡方法
### 希望する連絡方法
□ 電話
□ メール
□ 面談
□ その他：_________________

### 連絡可能時間
- 平日：_________________
- 土日祝：_________________
- 緊急時：_________________

## 添付書類
- [ ] 事件関連書類
- [ ] 証拠書類
- [ ] その他：_________________

## その他
### 特記事項
_________________

### 弁護士への要望
_________________

### 依頼人の状況
_________________

## 作成日
_________________

## 署名・捺印
依頼人：_________________ 印

## 注意事項
- この依頼書は、弁護士との協議の基礎資料です
- 依頼内容は、事件の進行に応じて変更される場合があります
- 報酬については、事前に十分に協議してください
- 守秘義務について確認してください
- 不明な点があれば、弁護士に確認してください`,

      'lawyer_selection_checklist': `# 弁護士選定チェックリスト

## 基本情報
- 選定日：_________________
- 事件名：_________________
- 選定者：_________________

## 1. 専門分野の確認
### 事件の種類
□ 民事事件（契約トラブル、損害賠償等）
□ 家事事件（離婚、相続等）
□ 労働事件（解雇、残業代等）
□ 刑事事件
□ 行政事件
□ その他：_________________

### 専門分野の適合性
- [ ] 該当分野の専門知識がある
- [ ] 類似事件の経験がある
- [ ] 専門資格を有している
- [ ] 専門団体に所属している
- [ ] その他：_________________

## 2. 実績・経験の確認
### 弁護士の実績
- [ ] 弁護士登録年数：_________________年
- [ ] 類似事件の解決実績：_________________件
- [ ] 成功事例の確認
- [ ] 敗訴事例の確認
- [ ] その他：_________________

### 経験の詳細
- [ ] 裁判所での経験
- [ ] 調停での経験
- [ ] 交渉での経験
- [ ] その他：_________________

## 3. 費用体系の確認
### 相談料
- [ ] 初回相談料：_________________円
- [ ] 継続相談料：_________________円/時間
- [ ] 無料相談の有無：□ 有　□ 無

### 着手金
- [ ] 着手金：_________________円
- [ ] 支払方法：_________________
- [ ] 分割払いの可否：□ 可　□ 不可

### 成功報酬
- [ ] 成功報酬率：_________________%
- [ ] 支払条件：_________________
- [ ] 最低・最高額の設定：_________________

### その他費用
- [ ] 交通費：_________________
- [ ] 通信費：_________________
- [ ] 印紙代：_________________
- [ ] その他：_________________

## 4. 対応可能時間の確認
### 平日の対応
- [ ] 対応可能時間：_________________
- [ ] 夜間対応：□ 可　□ 不可
- [ ] 土日対応：□ 可　□ 不可

### 緊急時の対応
- [ ] 緊急連絡可能：□ 可　□ 不可
- [ ] 緊急時連絡先：_________________
- [ ] 対応時間：_________________

## 5. コミュニケーションの確認
### 連絡方法
- [ ] 電話：□ 可　□ 不可
- [ ] メール：□ 可　□ 不可
- [ ] 面談：□ 可　□ 不可
- [ ] その他：_________________

### 報告頻度
- [ ] 定期的な報告：□ 有　□ 無
- [ ] 報告方法：_________________
- [ ] 報告頻度：_________________

## 6. 事務所・チームの確認
### 事務所の規模
- [ ] 所属弁護士数：_________________名
- [ ] 事務所の規模：□ 個人　□ 中小　□ 大規模
- [ ] 専門チームの有無：□ 有　□ 無

### サポート体制
- [ ] 事務員のサポート：□ 有　□ 無
- [ ] 専門スタッフ：□ 有　□ 無
- [ ] その他：_________________

## 7. 評判・信頼性の確認
### 評判の確認
- [ ] 知人からの紹介
- [ ] インターネットでの評判
- [ ] 弁護士会での評判
- [ ] その他：_________________

### 信頼性の確認
- [ ] 懲戒処分の有無：□ 有　□ 無
- [ ] 弁護士会での役職
- [ ] その他：_________________

## 8. 相性・信頼関係の確認
### 初回相談での印象
- [ ] 説明が分かりやすい
- [ ] 親身になってくれる
- [ ] 時間をかけてくれる
- [ ] その他：_________________

### 信頼できるか
- [ ] 信頼できる：□ 是　□ 否
- [ ] 理由：_________________

## 9. 比較検討
### 候補弁護士の比較
| 項目 | 弁護士A | 弁護士B | 弁護士C |
|------|---------|---------|---------|
| 専門分野 | | | |
| 経験年数 | | | |
| 相談料 | | | |
| 着手金 | | | |
| 成功報酬 | | | |
| 対応時間 | | | |
| 評判 | | | |

## 10. 最終判断
### 選定理由
1. _________________
2. _________________
3. _________________

### 決定
□ 依頼する
□ 依頼しない
□ さらに検討する

### 次回行動
_________________

## 注意事項
- このチェックリストは参考資料です
- 実際の選定では、複数の弁護士に相談することをお勧めします
- 費用については、事前に十分に確認してください
- 相性も重要な要素です
- 不明な点があれば、弁護士に直接確認してください`,

      'consultation_preparation_sheet': `# 弁護士相談準備シート

## 相談予定
- 相談日：_________________
- 相談時間：_________________
- 相談場所：_________________
- 弁護士名：_________________

## 1. 相談者の基本情報
### 個人情報
- 氏名：_________________
- 年齢：_________________
- 職業：_________________
- 住所：_________________
- 連絡先：_________________

### 家族・関係者
- 配偶者：_________________
- 子供：_________________
- その他関係者：_________________

## 2. 事件の概要
### 事件の種類
□ 民事事件（契約トラブル、損害賠償等）
□ 家事事件（離婚、相続等）
□ 労働事件（解雇、残業代等）
□ 刑事事件
□ 行政事件
□ その他：_________________

### 事件の概要
_________________

### 相手方の情報
- 氏名・名称：_________________
- 住所：_________________
- 連絡先：_________________
- 関係：_________________

## 3. 事件の経緯
### 発端（いつ、どこで、何が起きたか）
_________________

### 経過（時系列で整理）
1. _________________（_________________年___月）
2. _________________（_________________年___月）
3. _________________（_________________年___月）
4. _________________（_________________年___月）
5. _________________（_________________年___月）

### 現在の状況
_________________

### 今後の予定
_________________

## 4. 証拠書類の整理
### 持参する書類
- [ ] 契約書類
  - [ ] 契約書
  - [ ] 見積書
  - [ ] 請求書
  - [ ] 領収書
  - [ ] その他：_________________

- [ ] 通信記録
  - [ ] メール
  - [ ] 手紙
  - [ ] 電話記録
  - [ ] その他：_________________

- [ ] 写真・動画
  - [ ] 現場写真
  - [ ] 損害写真
  - [ ] 録音・録画
  - [ ] その他：_________________

- [ ] その他
  - [ ] 証人証言
  - [ ] 専門家意見書
  - [ ] その他：_________________

### 書類の整理方法
- 時系列順に整理
- 重要度順に整理
- 原本と写しを準備
- 目次を作成

## 5. 質問事項の準備
### 法的な質問
1. この事件は法的にどう評価されますか？
2. 勝訴の可能性はどの程度ですか？
3. どのような解決方法がありますか？
4. 時効の問題はありますか？

### 手続きに関する質問
1. どのような手続きが必要ですか？
2. 手続きの期間はどのくらいですか？
3. どのような書類が必要ですか？
4. 手続きの流れを教えてください。

### 費用に関する質問
1. 弁護士費用はどのくらいかかりますか？
2. 費用の支払い方法はどうなりますか？
3. 分割払いは可能ですか？
4. 成功報酬の条件はどうなりますか？

### その他の質問
1. _________________
2. _________________
3. _________________
4. _________________
5. _________________

## 6. 希望する解決方法
### 解決方法の希望
□ 和解・調停による解決
□ 訴訟による解決
□ その他：_________________

### 具体的な希望
- 解決金額：_________________円
- 解決期限：_________________
- その他：_________________

## 7. 予算・制約
### 予算
- 総予算：_________________円
- 月額予算：_________________円
- その他：_________________

### 制約事項
- 時間的制約：_________________
- 経済的制約：_________________
- その他：_________________

## 8. 相談後の行動計画
### 短期的な行動
1. _________________
2. _________________
3. _________________

### 中長期的な行動
1. _________________
2. _________________
3. _________________

## 9. メモ欄
### 相談中のメモ
_________________

### 弁護士からのアドバイス
_________________

### 次回相談予定
- 日時：_________________
- 準備事項：_________________

## 10. 注意事項
### 相談時の注意
- 時間を守る
- 証拠書類を整理して持参
- 質問事項を事前にまとめる
- メモを取る
- 分からないことは遠慮なく質問する

### 相談後の注意
- 弁護士からの指示を守る
- 必要な書類を準備する
- 定期的に連絡を取る
- 費用について確認する

## 作成日
_________________

## 備考
_________________`,

      'fee_estimate': `# 弁護士費用見積もり表

## 基本情報
- 見積日：_________________
- 事件名：_________________
- 依頼人：_________________
- 弁護士：_________________

## 1. 相談料
### 初回相談
- 料金：_________________円
- 時間：_________________時間
- 備考：_________________

### 継続相談
- 料金：_________________円/時間
- 予想回数：_________________回
- 予想総額：_________________円
- 備考：_________________

## 2. 着手金
### 基本着手金
- 金額：_________________円
- 支払期限：_________________
- 支払方法：_________________
- 備考：_________________

### 分割払い（該当する場合）
- 分割回数：_________________回
- 1回あたり：_________________円
- 支払間隔：_________________
- 備考：_________________

## 3. 成功報酬
### 報酬率
- 基本報酬率：_________________%
- 段階別報酬率：
  - _________________円まで：_________________%
  - _________________円まで：_________________%
  - _________________円以上：_________________%

### 支払条件
- 支払時期：_________________
- 最低額：_________________円
- 最高額：_________________円
- 備考：_________________

## 4. その他費用
### 実費
- 交通費：実費（概算：_________________円）
- 通信費：実費（概算：_________________円）
- 印紙代：実費（概算：_________________円）
- その他：_________________

### 専門家費用
- 鑑定料：_________________円
- 調査費：_________________円
- その他：_________________

## 5. 費用の内訳
### 事件の進行段階別
| 段階 | 期間 | 費用 | 備考 |
|------|------|------|------|
| 相談・調査 | _________________ | _________________円 | _________________ |
| 交渉・調停 | _________________ | _________________円 | _________________ |
| 訴訟準備 | _________________ | _________________円 | _________________ |
| 訴訟 | _________________ | _________________円 | _________________ |
| その他 | _________________ | _________________円 | _________________ |

### 月額概算
- 1ヶ月目：_________________円
- 2ヶ月目：_________________円
- 3ヶ月目：_________________円
- その他：_________________

## 6. 総費用見積もり
### 最安値シナリオ
- 相談料：_________________円
- 着手金：_________________円
- 成功報酬：_________________円
- その他費用：_________________円
- **合計：_________________円**

### 標準シナリオ
- 相談料：_________________円
- 着手金：_________________円
- 成功報酬：_________________円
- その他費用：_________________円
- **合計：_________________円**

### 最高値シナリオ
- 相談料：_________________円
- 着手金：_________________円
- 成功報酬：_________________円
- その他費用：_________________円
- **合計：_________________円**

## 7. 支払スケジュール
### 支払予定
1. 着手金：_________________（_________________円）
2. 月額費用：_________________（_________________円/月）
3. 成功報酬：_________________（_________________円）
4. その他：_________________

### 支払方法
- 銀行振込：_________________
- 現金：_________________
- その他：_________________

## 8. 費用の比較検討
### 他の弁護士との比較
| 弁護士 | 着手金 | 成功報酬 | 相談料 | 総額予想 |
|--------|--------|----------|--------|----------|
| 弁護士A | _________________円 | _________________% | _________________円 | _________________円 |
| 弁護士B | _________________円 | _________________% | _________________円 | _________________円 |
| 弁護士C | _________________円 | _________________% | _________________円 | _________________円 |

## 9. 費用対効果の検討
### 期待される成果
- 解決金額：_________________円
- 解決確率：_________________%
- 解決期間：_________________ヶ月

### 費用対効果
- 費用：_________________円
- 期待成果：_________________円
- 差額：_________________円
- 費用対効果：_________________%

## 10. 注意事項
### 費用に関する注意
- この見積もりは参考資料です
- 実際の費用は事件の進行により変動する場合があります
- 詳細な費用については、弁護士と事前に協議してください
- 支払い方法や期限についても確認してください

### その他の注意
- 弁護士費用は、事件の複雑さや弁護士の経験により大きく異なります
- 安いからといって良いとは限りません
- 高いからといって必ずしも良い結果が得られるとは限りません
- 弁護士との相性も重要な要素です

## 作成日
_________________

## 署名
依頼人：_________________ 印
弁護士：_________________ 印`
    };

    const content = templateContents[templateId];
    if (!content) {
      throw new Error(`テンプレートの内容が見つかりません: ${templateId}`);
    }

    // console.log('内联内容を使用');
    return content;
  }

  /**
   * MarkdownをHTMLに変換
   */
  static convertMarkdownToHtml(markdown: string): string {
    // 基本的なMarkdown変換（より高度な変換が必要な場合はmarked.jsなどを使用）
    let html = markdown
      // 見出し
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      // 太字
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // 斜体
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // リスト
      .replace(/^\* (.*$)/gim, '<li>$1</li>')
      .replace(/^- (.*$)/gim, '<li>$1</li>')
      // 段落
      .replace(/\n\n/g, '</p><p>')
      .replace(/^(?!<[h|li|ul|ol])(.*$)/gim, '<p>$1</p>');

    // リストの処理
    html = html.replace(/<li>.*?<\/li>/gs, (match) => {
      return `<ul>${match}</ul>`;
    });

    return `<div class="template-content">${html}</div>`;
  }

  /**
   * テンプレートをプレビュー用HTMLとして取得
   */
  static async getTemplatePreview(templateId: string): Promise<string> {
    // console.log('TemplateService.getTemplatePreview called with:', templateId);
    const content = await this.loadTemplateContent(templateId);
    // console.log('Template content loaded:', content.substring(0, 100) + '...');
    const html = this.convertMarkdownToHtml(content);
    // console.log('HTML converted:', html.substring(0, 100) + '...');
    return html;
  }

  /**
   * テンプレートをダウンロード用として取得
   */
  static async downloadTemplate(templateId: string, format: 'md' | 'html' | 'pdf' = 'md'): Promise<void> {
    const template = this.getTemplateById(templateId);
    if (!template) {
      throw new Error(`テンプレートが見つかりません: ${templateId}`);
    }

    const content = await this.loadTemplateContent(templateId);
    let downloadContent: string;
    let fileName: string;
    let mimeType: string;

    switch (format) {
      case 'html':
        downloadContent = this.convertMarkdownToHtml(content);
        fileName = `${template.name}.html`;
        mimeType = 'text/html';
        break;
      case 'pdf':
        // PDF変換は後で実装
        throw new Error('PDF変換機能は現在実装中です');
      default:
        downloadContent = content;
        fileName = `${template.name}.md`;
        mimeType = 'text/markdown';
    }

    // ファイルダウンロード
    const blob = new Blob([downloadContent], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

/**
 * 調停手続きガイド情報
 */
export const mediationGuide = {
  institutions: [
    {
      name: '簡易裁判所民事調停',
      description: '金銭トラブル、契約トラブル等',
      examples: ['売買代金の支払い', '賃貸借トラブル', '請負代金の支払い']
    },
    {
      name: '家庭裁判所調停',
      description: '離婚、相続、親子関係等の家事トラブル',
      examples: ['離婚調停', '相続調停', '親権調停']
    },
    {
      name: '法務省認証ADR',
      description: '幅広い民事トラブル、非公開・低コスト',
      examples: ['消費者トラブル', '医療過誤', '交通事故']
    },
    {
      name: '労働局あっせん',
      description: '労働問題（解雇、残業代、パワハラ等）',
      examples: ['解雇無効', '残業代請求', 'パワハラ解決']
    },
    {
      name: '消費者生活センター',
      description: '消費者トラブル',
      examples: ['商品の欠陥', '不当な契約', '訪問販売トラブル']
    }
  ],
  requiredDocuments: [
    '調停申立書（正本・副本）',
    '印鑑（認め印、スタンプ式は不可）',
    '手数料（収入印紙）',
    '郵便切手（手続きごとに必要）',
    '証拠書類の写し（1部）',
    '法人の場合は法人登記事項証明書'
  ],
  procedureSteps: [
    '申立書の作成・提出（窓口または郵送）',
    '調停期日の通知（通常1-2週間後）',
    '調停委員会での話し合い（1-3回程度）',
    '合意書の作成（調停成立の場合）',
    '調停の成立・終了'
  ],
  referenceLinks: [
    {
      name: '最高裁判所：民事調停で使う書式',
      url: 'https://www.courts.go.jp/saiban/syosiki/syosiki_minzityoutei/index.html'
    },
    {
      name: '最高裁判所：家事調停で使う書式',
      url: 'https://www.courts.go.jp/saiban/syosiki/syosiki_kazityoutei/index.html'
    },
    {
      name: '東京簡易裁判所：民事調停手続案内',
      url: 'https://www.courts.go.jp/tokyo-s/saiban/l3/Vcms3_00000345.html'
    }
  ]
};

/**
 * 自己訴訟手続きガイド情報
 */
export const litigationGuide = {
  courtTypes: [
    {
      name: '簡易裁判所',
      description: '140万円以下の金銭請求等の簡易な民事事件',
      examples: ['小額の金銭トラブル', '賃貸借トラブル', '交通事故の損害賠償']
    },
    {
      name: '地方裁判所',
      description: '一般的な民事訴訟事件',
      examples: ['契約トラブル', '不法行為', '債権回収']
    },
    {
      name: '家庭裁判所',
      description: '家事事件（離婚、相続等）',
      examples: ['離婚訴訟', '相続訴訟', '親権訴訟']
    }
  ],
  requiredDocuments: [
    '訴状（正本・副本）',
    '印鑑（認め印、スタンプ式は不可）',
    '手数料（収入印紙）',
    '証拠書類の写し（1部）',
    '委任状（代理人がいる場合）',
    '法人の場合は法人登記事項証明書'
  ],
  procedureSteps: [
    '訴状の作成・提出（窓口または郵送）',
    '訴状副本の送達（被告への通知）',
    '答弁書の提出（被告側）',
    '口頭弁論期日の設定',
    '準備書面の提出',
    '証拠調べの実施',
    '判決の言渡し'
  ],
  costs: [
    {
      name: '訴額100万円以下',
      fee: '1,000円'
    },
    {
      name: '訴額100万円超～500万円以下',
      fee: '2,000円'
    },
    {
      name: '訴額500万円超～1,000万円以下',
      fee: '3,000円'
    },
    {
      name: '訴額1,000万円超～5,000万円以下',
      fee: '5,000円'
    },
    {
      name: '訴額5,000万円超',
      fee: '10,000円'
    }
  ],
  referenceLinks: [
    {
      name: '最高裁判所：民事訴訟で使う書式',
      url: 'https://www.courts.go.jp/saiban/syosiki/syosiki_minzisosyo/index.html'
    },
    {
      name: '東京地方裁判所：民事訴訟手続案内',
      url: 'https://www.courts.go.jp/tokyo-s/saiban/l3/Vcms3_00000344.html'
    },
    {
      name: '法務省：民事訴訟法',
      url: 'https://elaws.e-gov.go.jp/document?lawid=408AC0000000109'
    }
  ]
};

/**
 * 弁護士依頼ガイド情報
 */
export const lawyerGuide = {
  lawyerTypes: [
    {
      name: '個人事務所',
      description: '個人で開業している弁護士',
      advantages: ['費用が比較的安い', '親身な対応', '柔軟な対応'],
      disadvantages: ['専門分野が限定的', '対応時間が限られる', '事務所の規模が小さい']
    },
    {
      name: '中小事務所',
      description: '数名の弁護士で構成される事務所',
      advantages: ['専門分野の多様性', 'チーム対応', '費用のバランス'],
      disadvantages: ['個人事務所より費用が高い', '担当弁護士が変わる可能性']
    },
    {
      name: '大規模事務所',
      description: '多数の弁護士で構成される事務所',
      advantages: ['高度な専門性', '豊富な実績', '充実したサポート体制'],
      disadvantages: ['費用が高い', '個人との距離感', '対応が画一的']
    }
  ],
  requiredDocuments: [
    '委任状',
    '委任契約書',
    '事件関連書類',
    '証拠書類',
    '身分証明書',
    '印鑑（認め印）'
  ],
  procedureSteps: [
    '弁護士の選定・相談',
    '委任契約の締結',
    '委任状の作成・提出',
    '事件の調査・分析',
    '解決方法の検討',
    '交渉・手続きの実施',
    '事件の解決'
  ],
  feeStructure: [
    {
      name: '相談料',
      description: '初回相談や継続相談の料金',
      typicalRange: '5,000円～20,000円/時間'
    },
    {
      name: '着手金',
      description: '事件を受任する際の初期費用',
      typicalRange: '10万円～100万円'
    },
    {
      name: '成功報酬',
      description: '事件解決時に支払う報酬',
      typicalRange: '解決金額の10%～30%'
    },
    {
      name: '月額報酬',
      description: '継続的な業務に対する月額報酬',
      typicalRange: '5万円～50万円/月'
    }
  ],
  selectionCriteria: [
    {
      name: '専門分野',
      description: '事件の種類に応じた専門知識',
      importance: '高'
    },
    {
      name: '実績・経験',
      description: '類似事件の解決実績',
      importance: '高'
    },
    {
      name: '費用',
      description: '弁護士費用の総額',
      importance: '中'
    },
    {
      name: '対応時間',
      description: '弁護士の対応可能時間',
      importance: '中'
    },
    {
      name: '相性',
      description: '弁護士とのコミュニケーション',
      importance: '高'
    }
  ],
  referenceLinks: [
    {
      name: '日本弁護士連合会：弁護士検索',
      url: 'https://www.nichibenren.or.jp/search.html'
    },
    {
      name: '法務省：弁護士法',
      url: 'https://laws.e-gov.go.jp/law/324AC1000000205'
    },
    {
      name: '日本弁護士連合会：弁護士費用について',
      url: 'https://www.nichibenren.or.jp/legal_advice/cost.html'
    }
  ]
}; 